import React from "react";
import { CheckCircle2, ArrowLeft, MessageCircle, Sparkles } from "lucide-react";
import {
  STRIPE_CONSULTATION_URL,
  buildWhatsAppLink,
  CONSULTATION_WA_MESSAGE,
  FULL_SUPPORT_WA_MESSAGE,
} from "../lib/constants";

const CONSULT = {
  title: "الاستشارة",
  price: "100€",
  tagline: "جواب واضح وخطة عمل",
  desc: "لمن يفضّل أن يقوم بالإجراءات بنفسه، لكنه يحتاج أولاً أن يعرف ما هو المنطقي وما الذي يجب أن يبدأ به.",
  bullets: [
    "تقييم لحالتك",
    "توصية بالمسار الصحيح للإقامة",
    "قائمة المستندات الأولية",
    "أهم المخاطر والخطوات التالية",
  ],
  cta: "احجز استشارة · 100€",
  href: STRIPE_CONSULTATION_URL,
  testid: "offer-consultation",
};

const FULL = {
  title: "الدعم الكامل",
  price: "500€",
  tagline: "نمشي معك في الإجراءات",
  desc: "لمن يريد توفير الوقت، وتقليل الضغط، والسير في العملية مع توجيه عملي في كل خطوة.",
  bullets: [
    "تحليل شامل لحالتك",
    "إرشاد خطوة بخطوة",
    "دعم في تجهيز ملف الإجراءات",
    "مرافقة عبر المراحل الأساسية",
  ],
  cta: "اطلب الدعم الكامل · 500€",
  href: buildWhatsAppLink(FULL_SUPPORT_WA_MESSAGE),
  testid: "offer-full",
};

export default function Offers() {
  return (
    <section
      id="offers"
      className="py-20 lg:py-24 bg-white"
      data-testid="offers-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-start max-w-2xl mb-12">
          <div className="text-emerald-700 text-sm font-semibold mb-3">
            الخيارات المتاحة
          </div>
          <h2 className="font-tajawal text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
            اختر ما يناسبك: جواب واضح أو دعم كامل
          </h2>
          <p className="mt-3 text-base md:text-lg text-slate-600">
            لا نفرض خدمة كاملة على الجميع. إذا كانت الاستشارة تكفيك، سنقولها
            لك مباشرة.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {/* Consultation card */}
          <article
            className="relative bg-white border border-slate-200 rounded-2xl p-7 lg:p-9 shadow-sm hover:shadow-md transition-shadow"
            data-testid={`${CONSULT.testid}-card`}
          >
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <h3 className="font-tajawal text-2xl font-bold text-slate-900">
                  {CONSULT.title}
                </h3>
                <div className="text-sm text-slate-500 mt-1">
                  {CONSULT.tagline}
                </div>
              </div>
              <div className="text-3xl font-tajawal font-extrabold text-slate-900">
                {CONSULT.price}
              </div>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed mb-6">
              {CONSULT.desc}
            </p>
            <ul className="space-y-3 mb-7">
              {CONSULT.bullets.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-sm text-slate-800"
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <a
              href={CONSULT.href}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 btn-slate rounded-xl px-5 py-3.5 font-semibold"
              data-testid={`${CONSULT.testid}-cta`}
            >
              {CONSULT.cta}
              <ArrowLeft className="w-4 h-4" />
            </a>
            <p className="mt-3 text-xs text-slate-500 text-center">
              الدفع الآمن عبر Stripe
            </p>
          </article>

          {/* Full support card - featured */}
          <article
            className="relative bg-slate-900 text-white rounded-2xl p-7 lg:p-9 shadow-lg overflow-hidden"
            data-testid={`${FULL.testid}-card`}
          >
            <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 bg-emerald-500 text-slate-900 px-3 py-1 rounded-full text-xs font-bold">
              <Sparkles className="w-3.5 h-3.5" />
              الأكثر طلباً
            </div>
            <div className="flex items-baseline justify-between mb-4 pt-6">
              <div>
                <h3 className="font-tajawal text-2xl font-bold">
                  {FULL.title}
                </h3>
                <div className="text-sm text-slate-300 mt-1">
                  {FULL.tagline}
                </div>
              </div>
              <div className="text-3xl font-tajawal font-extrabold text-emerald-400">
                {FULL.price}
              </div>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              {FULL.desc}
            </p>
            <ul className="space-y-3 mb-7">
              {FULL.bullets.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-sm text-slate-100"
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <a
              href={FULL.href}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 btn-accent rounded-xl px-5 py-3.5 font-semibold"
              data-testid={`${FULL.testid}-cta`}
            >
              <MessageCircle className="w-4 h-4" />
              {FULL.cta}
            </a>
            <p className="mt-3 text-xs text-slate-400 text-center">
              التواصل يتم عبر واتساب لتنسيق التفاصيل
            </p>
          </article>
        </div>

        {/* Small consultation booking w/ Stripe note */}
        <div className="mt-8 flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
          <div className="w-10 h-10 rounded-xl bg-white text-emerald-700 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            بعد الدفع ستصلك رسالة تأكيد مع رابط لتحديد موعد الاستشارة، وسيتم
            التواصل معك لجمع المعلومات الأساسية قبل الجلسة.
          </p>
        </div>
      </div>
    </section>
  );
}
